import { AxiosError } from 'axios';
import { api } from '@/Data/api';
import ss from '../Session';
import type { Application } from 'ai-api/types/admission';
import type { AdmissionApiResponse, CreateApplicationRequest, UpdateApplicationRequest, ApplicationListParams } from './index';
import { getStoredLanguage } from '@/Components/LanguageSelector';

/**
 * Create a new application
 * @param params CreateApplicationRequest with application data
 * @returns Promise with success status
 */
export const createApplicationAPI = async (params: CreateApplicationRequest): Promise<boolean> => {
    try {
        if (!params.applicationid || !params.universityid || !params.trackid) {
            throw new Error('Application ID, University ID, and Track ID are required');
        }

        const res = await api.post<AdmissionApiResponse<Application>>(
            '/admission/application',
            params,
            {
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`,
                    'Accept-Language': getStoredLanguage()
                }
            }
        );

        return res.data?.code === 200 || res.data?.code === 201;
    } catch (e) {
        console.error('Failed to create application:', e);
        return false;
    }
};

/**
 * Update an existing application
 * @param params UpdateApplicationRequest with updated data
 * @returns Promise with success status
 */
export const updateApplicationAPI = async (params: UpdateApplicationRequest): Promise<boolean> => {
    try {
        if (!params.applicationid) {
            throw new Error('Application ID is required');
        }

        const res = await api.put<AdmissionApiResponse<Application>>(
            '/admission/application',
            params,
            {
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`,
                    'Accept-Language': getStoredLanguage()
                }
            }
        );

        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to update application:', e);
        return false;
    }
};

/**
 * List applications for a university track
 * @param params ApplicationListParams
 * @returns Promise with applications array or null
 */
export const listApplicationsAPI = async (params: ApplicationListParams): Promise<Application[] | null> => {
    try {
        const res = await api.get<AdmissionApiResponse<Application[]>>(
            '/admission/application',
            {
                params,
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`,
                    'Accept-Language': getStoredLanguage()
                }
            }
        );

        if (res.data?.code === 200) {
            return res.data.data || [];
        }

        return null;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 404) {
            console.warn('Applications not found:', params);
            return [];
        }
        console.error('Failed to list applications:', e);
        return null;
    }
};

/**
 * Delete an application
 * @param applicationid Application ID to delete
 * @returns Promise with success status
 */
export const deleteApplicationAPI = async (applicationid: string): Promise<boolean> => {
    try {
        if (!applicationid) {
            throw new Error('Application ID is required');
        }

        const res = await api.delete<AdmissionApiResponse<{success: boolean}>>(
            '/admission/application',
            {
                params: { applicationid },
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`,
                    'Accept-Language': getStoredLanguage()
                }
            }
        );

        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to delete application:', e);
        return false;
    }
};